import { partition, zip } from 'lodash';
import { IsEqual } from 'type-fest';
import { z } from 'zod';

import {
  AddressAppraisalCreateFields,
  convertStringToDate,
  CreateLoanRow,
  exhaustiveStringTuple,
  Expect,
} from '@willow/types-iso';

import { assert } from '../../../../utils/assert';
import { LoanWarn } from '../../../shared/apiWarnings';
import { getParsedFields } from '../CreateLoanCrossFieldParser';

type AppraisalEntry = {
  field: any;
  path: string;
};

// Generic over the emitter: the cross-field parser passes rules that raise errors through a
// RefinementCtx, the warning parser passes rules that can only raise catalogued warnings.
type UnaryAppraisalValidationFn<TEmit> = (appraisal: AppraisalEntry, emit: TEmit) => void;
type BinaryAppraisalValidationFn<TEmit> = (
  originalAppraisal: AppraisalEntry,
  correspondingAppraisal: AppraisalEntry,
  emit: TEmit,
) => void;

type ExcludedAppraisalFields = 'orderId';
type RequiredAppraisalFields = keyof Pick<AddressAppraisalCreateFields, 'amount' | 'date' | 'type' | 'method'>;

const REQUIRED_APPRAISAL_FIELDS = exhaustiveStringTuple<RequiredAppraisalFields>()(
  'amount',
  'date',
  'type',
  'method',
) as readonly RequiredAppraisalFields[];

// Compile-time guarantee: All fields in AddressAppraisalCreateFields must be either required or explicitly excluded
// If a new field is added to AddressAppraisalCreateFields but not accounted for here, this will cause a type error
export type AllAppraisalFieldsAccounted = Expect<
  IsEqual<keyof AddressAppraisalCreateFields, RequiredAppraisalFields | ExcludedAppraisalFields>
>;

export const validateAppraisalFields =
  <TEmit>(validations: (UnaryAppraisalValidationFn<TEmit> | BinaryAppraisalValidationFn<TEmit>)[]) =>
  (data: CreateLoanRow, emit: TEmit) => {
    const parsed = getParsedFields(data, [
      'addressAppraisal',
      'address2Appraisal',
      'address3Appraisal',
      'address4Appraisal',
      'address5Appraisal',
      'address6Appraisal',
      'address7Appraisal',
      'address8Appraisal',
      'address9Appraisal',
      'address10Appraisal',
      'addressOriginalAppraisal',
      'address2OriginalAppraisal',
      'address3OriginalAppraisal',
      'address4OriginalAppraisal',
      'address5OriginalAppraisal',
      'address6OriginalAppraisal',
      'address7OriginalAppraisal',
      'address8OriginalAppraisal',
      'address9OriginalAppraisal',
      'address10OriginalAppraisal',
    ]);

    if (parsed == null) {
      return;
    }

    const {
      addressAppraisal,
      address2Appraisal,
      address3Appraisal,
      address4Appraisal,
      address5Appraisal,
      address6Appraisal,
      address7Appraisal,
      address8Appraisal,
      address9Appraisal,
      address10Appraisal,
      addressOriginalAppraisal,
      address2OriginalAppraisal,
      address3OriginalAppraisal,
      address4OriginalAppraisal,
      address5OriginalAppraisal,
      address6OriginalAppraisal,
      address7OriginalAppraisal,
      address8OriginalAppraisal,
      address9OriginalAppraisal,
      address10OriginalAppraisal,
    } = parsed;

    const appraisals = [
      { field: addressAppraisal, path: 'addressAppraisal' },
      { field: address2Appraisal, path: 'address2Appraisal' },
      { field: address3Appraisal, path: 'address3Appraisal' },
      { field: address4Appraisal, path: 'address4Appraisal' },
      { field: address5Appraisal, path: 'address5Appraisal' },
      { field: address6Appraisal, path: 'address6Appraisal' },
      { field: address7Appraisal, path: 'address7Appraisal' },
      { field: address8Appraisal, path: 'address8Appraisal' },
      { field: address9Appraisal, path: 'address9Appraisal' },
      { field: address10Appraisal, path: 'address10Appraisal' },
    ];
    const originalAppraisals = [
      { field: addressOriginalAppraisal, path: 'addressOriginalAppraisal' },
      { field: address2OriginalAppraisal, path: 'address2OriginalAppraisal' },
      { field: address3OriginalAppraisal, path: 'address3OriginalAppraisal' },
      { field: address4OriginalAppraisal, path: 'address4OriginalAppraisal' },
      { field: address5OriginalAppraisal, path: 'address5OriginalAppraisal' },
      { field: address6OriginalAppraisal, path: 'address6OriginalAppraisal' },
      { field: address7OriginalAppraisal, path: 'address7OriginalAppraisal' },
      { field: address8OriginalAppraisal, path: 'address8OriginalAppraisal' },
      { field: address9OriginalAppraisal, path: 'address9OriginalAppraisal' },
      { field: address10OriginalAppraisal, path: 'address10OriginalAppraisal' },
    ];
    assert(
      appraisals.length === originalAppraisals.length,
      'Appraisals and original appraisals must have the same length',
    );

    const zippedAppraisals = zip(appraisals, originalAppraisals);

    // fn.length is, weirdly, the number of arguments it takes
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/length
    const [unaryValidations, binaryValidations] = partition(
      validations,
      // unary validator (w.r.t. appraisals) has two arguments
      (v): v is UnaryAppraisalValidationFn<TEmit> => v.length === 2,
    );

    zippedAppraisals.forEach(([a, oA]) => {
      // not-null assertions because lodash.zip will always give us zip(A[], B[]): [A | undefined, B | undefined][]
      // even for these equally-sized lists
      const appraisal = a!;
      const originalAppraisal = oA!;

      unaryValidations.forEach((v) => v(appraisal, emit));
      unaryValidations.forEach((v) => v(originalAppraisal, emit));
      binaryValidations.forEach((v) => v(originalAppraisal, appraisal, emit));
    });
  };

export function validateAppraisalHasRequiredFieldsOrNone(appraisalEntry: AppraisalEntry, warn: LoanWarn) {
  const { field, path } = appraisalEntry;
  // Check that all or no required appraisal fields are filled in
  const presentFields = REQUIRED_APPRAISAL_FIELDS.filter((f) => field?.[f] != null);
  if (presentFields.length > 0 && presentFields.length < REQUIRED_APPRAISAL_FIELDS.length) {
    REQUIRED_APPRAISAL_FIELDS.forEach((fieldName) => {
      if (field?.[fieldName] == null) {
        if (fieldName === 'type') {
          warn('APPRAISAL_TYPE_DEFAULTED', { path: [`${path}.${fieldName}`] });
        } else {
          warn('APPRAISAL_INCOMPLETE', {
            message: `Appraisal ${fieldName} is required when adding a property appraisal`,
            path: [`${path}.${fieldName}`],
          });
        }
      }
    });
  }
}

export function validateOriginalAppraisalDateIsBeforeCorrespondingAppraisalDate(
  originalAppraisalEntry: AppraisalEntry,
  correspondingAppraisalEntry: AppraisalEntry,
  ctx: z.RefinementCtx,
) {
  const originalAppraisalDate = originalAppraisalEntry.field?.date
    ? convertStringToDate(originalAppraisalEntry.field.date)
    : undefined;
  const correspondingAppraisalDate = correspondingAppraisalEntry.field?.date
    ? convertStringToDate(correspondingAppraisalEntry.field.date)
    : undefined;
  if (originalAppraisalDate && correspondingAppraisalDate && originalAppraisalDate > correspondingAppraisalDate) {
    ctx.addIssue({
      code: 'custom',
      message: 'Original appraisal date must be equal to or prior to the appraisal date',
      path: [`${originalAppraisalEntry.path}.date`],
    });
  }
}
